import { EditableRecordSchema } from '@erp/json-renderer/apps';
import type { EditableRecord } from '@erp/json-renderer/apps';
import type { CompiledBehaviorInstance } from '@erp/shared-types';
import type { AppInvocationSession } from './appController';
import { AppActionError } from './appController';
import { asRecord } from './appBehaviorPayload';

/**
 * One operation's view of the App: its own state, its events, its bound
 * command and the delivery session of the consumer it serves. Runners stay
 * small and typed; the host owns identity, tracing and event dispatch.
 */
export interface OperationScope {
  readonly instance: CompiledBehaviorInstance;
  readonly operationId: number;
  readonly parentOperationId: number | null;
  /** The instance's own state, read live. */
  read(key: string): unknown;
  /** Publish own state. The host records which paths changed. */
  write(changes: Record<string, unknown>): void;
  /** Report a fact after the operation's state is published. */
  emit(event: string, payload: unknown): void;
  /** Run the command bound to a service slot through the App. */
  command(slot: string, params: Record<string, unknown>): Promise<unknown>;
  openSession(): AppInvocationSession;
  retireSession(): void;
  /** Bumps on every open; a captured value identifies the editor generation. */
  sessionId(): number | null;
}

export type BehaviorRunner = (scope: OperationScope, input: unknown) => unknown;

function busy(message: string): AppActionError {
  return new AppActionError({ reason: 'busy', message });
}

function invalid(message: string): AppActionError {
  return new AppActionError({ reason: 'invalid', message });
}

function recordOf(input: unknown): EditableRecord {
  const parsed = EditableRecordSchema.safeParse(asRecord(input)?.record);
  if (!parsed.success)
    throw invalid(
      'This control did not send a record with an id and one text value.',
    );
  return parsed.data;
}

function textOf(input: unknown): string {
  const text = asRecord(input)?.text;
  if (typeof text !== 'string')
    throw invalid('This control did not send a text value.');
  return text;
}

/** The editor generation a save belongs to, captured when Save starts. */
interface CapturedSave {
  readonly recordId: string;
  readonly draft: string;
  readonly session: number | null;
}

function capturedSave(scope: OperationScope): CapturedSave {
  if (scope.read('open') !== true)
    throw invalid('Open a record before saving it.');
  if (scope.read('pending') === true)
    throw busy('Wait for the current save to finish.');
  const recordId = scope.read('recordId');
  if (typeof recordId !== 'string')
    throw invalid('Open a record before saving it.');
  const draft = scope.read('draft');
  return {
    recordId,
    draft: typeof draft === 'string' ? draft : '',
    session: scope.sessionId(),
  };
}

function stillCurrent(scope: OperationScope, captured: CapturedSave): boolean {
  return (
    scope.read('open') === true &&
    scope.read('session') === captured.session &&
    scope.read('recordId') === captured.recordId
  );
}

function finishSave(scope: OperationScope, captured: CapturedSave): string {
  if (!stillCurrent(scope, captured)) return captured.draft;
  scope.write({ pending: false, saved: captured.draft, error: null });
  scope.emit('saved', {
    record: { id: captured.recordId, text: captured.draft },
  });
  return captured.draft;
}

function failSave(
  scope: OperationScope,
  captured: CapturedSave,
  error: unknown,
): never {
  if (stillCurrent(scope, captured)) {
    scope.write({ pending: false, error: messageOf(error) });
  }
  throw error;
}

export function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

const selection: Record<string, BehaviorRunner> = {
  select(scope, input) {
    const record = recordOf(input);
    scope.write({ selected: record });
    scope.emit('selected', { record });
  },
  clear(scope) {
    scope.write({ selected: null });
  },
};

const editor: Record<string, BehaviorRunner> = {
  open(scope, input) {
    const record = recordOf(input);
    if (scope.read('open') === true && scope.read('pending') === true)
      throw busy('Wait for the current save before opening another record.');
    scope.retireSession();
    const session = scope.openSession();
    scope.write({
      open: true,
      recordId: record.id,
      draft: record.text,
      saved: null,
      pending: false,
      error: null,
      session: session.id,
    });
  },
  changeDraft(scope, input) {
    const text = textOf(input);
    if (scope.read('open') !== true)
      throw invalid('Open a record before editing it.');
    if (scope.read('pending') === true)
      throw busy('Wait for the current save before editing.');
    scope.write({ draft: text });
  },
  save(scope) {
    const captured = capturedSave(scope);
    scope.write({ pending: true, error: null });
    return scope
      .command('save', { id: captured.recordId, text: captured.draft })
      .then(
        () => finishSave(scope, captured),
        (error: unknown) => failSave(scope, captured, error),
      );
  },
  close(scope) {
    scope.retireSession();
    scope.write({
      open: false,
      recordId: null,
      draft: '',
      saved: null,
      pending: false,
      error: null,
      session: 0,
    });
  },
};

/** The implementation of every registered definition, by definition id. */
export const BEHAVIOR_RUNNERS: Readonly<
  Record<string, Readonly<Record<string, BehaviorRunner>>>
> = {
  'record-selection': selection,
  'record-editor': editor,
};

/** The operation a direct write to this own-state key means, if any. */
export const BOUND_WRITE_ROUTES: Readonly<
  Record<string, Readonly<Record<string, { operation: string; input: string }>>>
> = {
  'record-selection': {
    selected: { operation: 'select', input: 'record' },
  },
  'record-editor': {
    draft: { operation: 'changeDraft', input: 'text' },
  },
};
